import type { Game } from './types'

export const SIMULATION_DURATION_MS = 60000
export const SIMULATED_MATCH_DURATION_MS = 6000

export interface SimulationScheduleEntry {
  gameId: string
  startMs: number
  revealMs: number
}

export type SimGameStatus = 'scheduled' | 'live' | 'final'

/**
 * Compresses a gameweek's real kickoff times into a fixed-length playback
 * window, preserving relative spacing between kickoff groups so fixtures
 * that kicked off later in real life also start later in the simulation.
 */
export function buildSimulationSchedule(
  games: Game[]
): SimulationScheduleEntry[] {
  if (games.length === 0) return []

  const kickoffs = games.map((game) => new Date(game.kickoff).getTime())
  const minKickoff = Math.min(...kickoffs)
  const maxKickoff = Math.max(...kickoffs)
  const span = maxKickoff - minKickoff
  const startWindowMs = SIMULATION_DURATION_MS - SIMULATED_MATCH_DURATION_MS
  const scale = span === 0 ? 0 : startWindowMs / span

  return games.map((game, index) => {
    const startMs = (kickoffs[index] - minKickoff) * scale
    return {
      gameId: game.id,
      startMs,
      revealMs: startMs + SIMULATED_MATCH_DURATION_MS,
    }
  })
}

export function getSimGameStatus(
  entry: SimulationScheduleEntry | undefined,
  elapsedMs: number
): SimGameStatus | null {
  if (!entry) return null
  if (elapsedMs < entry.startMs) return 'scheduled'
  if (elapsedMs < entry.revealMs) return 'live'
  return 'final'
}

export function buildSimGameStatuses(
  schedule: SimulationScheduleEntry[],
  elapsedMs: number
): Record<string, SimGameStatus> {
  const statuses: Record<string, SimGameStatus> = {}
  for (const entry of schedule) {
    statuses[entry.gameId] = getSimGameStatus(entry, elapsedMs)!
  }
  return statuses
}

/**
 * Drives a 30s (real time) playback loop, ticking `onTick` with elapsed ms
 * and calling `onComplete` once the window ends. Returns a cleanup function
 * that cancels the loop early.
 */
export function startSimulationLoop(
  onTick: (elapsedMs: number) => void,
  onComplete: () => void
): () => void {
  const startTime = Date.now()
  const intervalId = setInterval(() => {
    const elapsed = Date.now() - startTime
    if (elapsed >= SIMULATION_DURATION_MS) {
      clearInterval(intervalId)
      onTick(SIMULATION_DURATION_MS)
      onComplete()
      return
    }
    onTick(elapsed)
  }, 100)
  return () => clearInterval(intervalId)
}

export interface GoalEvent {
  atMs: number
  scorer: 'home' | 'away'
}

// Simple string hash -> a numeric seed for the LCG below, so the same
// game.id always kicks off the same pseudo-random sequence (deterministic
// goal timing across renders/tests without needing real randomness).
function hashStringToSeed(value: string): number {
  let hash = 0
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0
  }
  return hash
}

// Park-Miller LCG — deterministic given a seed, produces values in [0, 1).
function createSeededRandom(seed: number): () => number {
  let state = seed % 2147483647
  if (state <= 0) state += 2147483646
  return () => {
    state = (state * 16807) % 2147483647
    return (state - 1) / 2147483646
  }
}

/**
 * Builds the sequence of goal "moments" for a single game's live window,
 * matching its known final score. Each goal lands in its own evenly-spaced
 * sub-window of (startMs, revealMs) with random jitter inside that
 * sub-window, which guarantees ascending, strictly-inside-bounds timestamps
 * without needing to sort or de-duplicate randomly-drawn values.
 */
export function buildGoalTimeline(
  game: Game,
  actualResult: { homeScore: number; awayScore: number },
  entry: SimulationScheduleEntry
): GoalEvent[] {
  const totalGoals = actualResult.homeScore + actualResult.awayScore
  if (totalGoals === 0) return []

  const random = createSeededRandom(hashStringToSeed(game.id))
  const windowMs = entry.revealMs - entry.startMs
  const slotMs = windowMs / totalGoals
  const paddingMs = slotMs * 0.1

  const timestamps: number[] = []
  for (let i = 0; i < totalGoals; i++) {
    const slotStart = entry.startMs + i * slotMs
    const jitter = random() * (slotMs - 2 * paddingMs)
    timestamps.push(slotStart + paddingMs + jitter)
  }

  const scorers: ('home' | 'away')[] = [
    ...Array(actualResult.homeScore).fill('home' as const),
    ...Array(actualResult.awayScore).fill('away' as const),
  ]
  for (let i = scorers.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1))
    ;[scorers[i], scorers[j]] = [scorers[j], scorers[i]]
  }

  return timestamps.map((atMs, index) => ({ atMs, scorer: scorers[index] }))
}

/**
 * Reduces a goal timeline into the running score as of `elapsedMs` — drives
 * the incremental scoreline shown during a game's `live` status.
 */
export function getLiveScoreAt(
  timeline: GoalEvent[],
  elapsedMs: number
): { homeScore: number; awayScore: number } {
  let homeScore = 0
  let awayScore = 0
  for (const event of timeline) {
    if (event.atMs > elapsedMs) break
    if (event.scorer === 'home') homeScore++
    else awayScore++
  }
  return { homeScore, awayScore }
}

/** Maps a game's compressed sim window onto a 1-90 display minute. */
export function getSimLiveMinute(
  entry: SimulationScheduleEntry,
  elapsedMs: number
): number {
  const windowMs = entry.revealMs - entry.startMs
  if (windowMs <= 0) return 90
  const progress = Math.min(Math.max((elapsedMs - entry.startMs) / windowMs, 0), 1)
  return Math.max(1, Math.round(progress * 90))
}

export interface SimGameweekState {
  statuses: Record<string, SimGameStatus>
  liveResults: Record<string, { homeScore: number; awayScore: number }>
  liveMinutes: Record<string, number>
}

/**
 * Single derivation of a gameweek's simulated live state (status, running
 * score, display minute) from the raw schedule/timeline — the one call site
 * both GameweekGrid (via App.tsx) and the Fixtures page read from, so a live
 * fixture can never drift between the two screens.
 */
export function deriveSimGameweekState(
  schedule: SimulationScheduleEntry[] | null,
  elapsedMs: number,
  goalTimelines: Record<string, GoalEvent[]>
): SimGameweekState | null {
  if (!schedule) return null

  const statuses = buildSimGameStatuses(schedule, elapsedMs)
  const liveResults: Record<string, { homeScore: number; awayScore: number }> = {}
  const liveMinutes: Record<string, number> = {}
  for (const entry of schedule) {
    liveResults[entry.gameId] = getLiveScoreAt(
      goalTimelines[entry.gameId] ?? [],
      elapsedMs
    )
    liveMinutes[entry.gameId] = getSimLiveMinute(entry, elapsedMs)
  }
  return { statuses, liveResults, liveMinutes }
}
